// src/pages/customers/[id]/tabs/visits.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../../../utils/config";

function fmtDateTime(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
}

export default function CustomerVisits() {
  const params = useParams();
  const customerId = params.customerId || params.id; // works for /customers/:id/visits or /customers/:customerId/visit
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  function normalize(list = []) {
    return list
      .map((v) => ({
        id: v.id,
        when: v.visited_at || v.visit_time || v.created_at || v.updated_at,
        salesperson_name: v.salesperson_name || v.user_name || v.salesperson || "",
        lat: typeof v.latitude === "string" ? parseFloat(v.latitude) : v.latitude,
        lng: typeof v.longitude === "string" ? parseFloat(v.longitude) : v.longitude,
        accuracy_m: v.accuracy_m ?? null,
        distance_km: v.distance_km ?? null,
        remarks: v.remarks || v.notes || "",
        photo_url: v.photo_url || v.selfie_url || null,
      }))
      .sort((a, b) => new Date(b.when) - new Date(a.when));
  }

  async function fetchData() {
    setLoading(true);
    setErr("");
    try {
      const endpoints = [
        `/visit/customer/${customerId}`,
        `/api/visit/customer/${customerId}`,
        `/api/visits/customer/${customerId}`,
      ];
      let data = null;
      let lastErr = null;

      for (const ep of endpoints) {
        try {
          const r = await axios.get(`${API_BASE_URL}${ep}`, { headers });
          data = r.data;
          break;
        } catch (e) {
          lastErr = e;
          if (e?.response?.status === 404) continue;
        }
      }

      if (!data) throw lastErr || new Error("No endpoint returned data");

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.rows)
        ? data.rows
        : data.data && Array.isArray(data.data)
        ? data.data
        : [];

      setRows(normalize(list));
    } catch (e) {
      console.error(e);
      setErr("Failed to load visits for this customer.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!customerId) {
      console.warn("CustomerVisits: missing param id/customerId", params);
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Visits</h2>

        {/* Desktop button */}
        <Link
          to={`/visits/mark?customerId=${encodeURIComponent(customerId || "")}`}
          className="hidden sm:inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
        >
          + Mark Visit
        </Link>
      </div>

      {/* Mobile button */}
      <Link
        to={`/visits/mark?customerId=${encodeURIComponent(customerId || "")}`}
        className="sm:hidden mb-3 inline-flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
      >
        + Mark Visit
      </Link>

      {err && (
        <div className="mb-3 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
          {err}
        </div>
      )}

      {/* ===== MOBILE: card list ===== */}
      <div className="sm:hidden space-y-3">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            Loading…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-500">
            No visits yet.
          </div>
        )}

        {!loading &&
          rows.map((r) => {
            const photoAbs = r.photo_url
              ? r.photo_url.startsWith("http")
                ? r.photo_url
                : `${API_BASE_URL}${r.photo_url.startsWith("/") ? r.photo_url : "/" + r.photo_url}`
              : null;
            const gmaps =
              typeof r.lat === "number" && typeof r.lng === "number"
                ? `https://www.google.com/maps?q=${r.lat},${r.lng}`
                : null;

            return (
              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                {/* Top row: when + salesperson + selfie */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{fmtDateTime(r.when)}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                        {r.salesperson_name || "—"}
                      </span>
                      {r.accuracy_m != null && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          ± {r.accuracy_m} m
                        </span>
                      )}
                      {r.distance_km != null && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                          {r.distance_km} km
                        </span>
                      )}
                    </div>
                  </div>

                  {photoAbs ? (
                    <a href={photoAbs} target="_blank" rel="noreferrer" className="shrink-0">
                      <img
                        src={photoAbs}
                        alt="selfie"
                        className="h-14 w-14 rounded-md object-cover border border-slate-200"
                      />
                    </a>
                  ) : (
                    <div className="text-xs text-slate-400">No selfie</div>
                  )}
                </div>

                {/* Location + map */}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-slate-600">
                    {typeof r.lat === "number" && typeof r.lng === "number" ? (
                      <span className="font-mono">
                        {r.lat.toFixed(6)}, {r.lng.toFixed(6)}
                      </span>
                    ) : (
                      <span className="text-slate-400">No coordinates</span>
                    )}
                  </div>

                  {gmaps ? (
                    <a
                      href={gmaps}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
                    >
                      View Map
                    </a>
                  ) : null}
                </div>

                {/* Remarks */}
                <div className="mt-2 text-sm text-slate-700">
                  {r.remarks ? (
                    <div className="line-clamp-3">{r.remarks}</div>
                  ) : (
                    <span className="text-slate-400">No remarks</span>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* ===== DESKTOP/TABLET: table ===== */}
      <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-700">
            <tr>
              <th className="px-3 py-2">Date/Time</th>
              <th className="px-3 py-2">Salesperson</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Accuracy</th>
              <th className="px-3 py-2">Distance</th>
              <th className="px-3 py-2">Selfie</th>
              <th className="px-3 py-2">Remarks</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4" colSpan={8}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-4" colSpan={8}>
                  No visits yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const photoAbs = r.photo_url
                  ? r.photo_url.startsWith("http")
                    ? r.photo_url
                    : `${API_BASE_URL}${r.photo_url.startsWith("/") ? r.photo_url : "/" + r.photo_url}`
                  : null;
                const gmaps =
                  typeof r.lat === "number" && typeof r.lng === "number"
                    ? `https://www.google.com/maps?q=${r.lat},${r.lng}`
                    : null;

                return (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(r.when)}</td>
                    <td className="px-3 py-2">{r.salesperson_name || "—"}</td>
                    <td className="px-3 py-2">
                      {typeof r.lat === "number" && typeof r.lng === "number" ? (
                        <span className="font-mono text-xs">
                          {r.lat.toFixed(6)}, {r.lng.toFixed(6)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.accuracy_m != null ? `${r.accuracy_m} m` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.distance_km != null ? `${r.distance_km} km` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {photoAbs ? (
                        <a href={photoAbs} target="_blank" rel="noreferrer">
                          <img
                            src={photoAbs}
                            alt="selfie"
                            className="h-12 w-12 rounded-md object-cover border border-slate-200"
                          />
                        </a>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-[28ch] truncate" title={r.remarks}>
                      {r.remarks || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {gmaps ? (
                        <a
                          href={gmaps}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-md border px-2 py-1 hover:bg-slate-50"
                        >
                          View Map
                        </a>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
